import { ReservedResource, UserScope } from '@logto/core-kit';
import { type ConsentInfoResponse, isCimdClientId } from '@logto/schemas';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { consent, getConsentInfo } from '@/apis/consent';
import UserAvatar from '@/assets/icons/default-user-avatar.svg?react';
import { Icon, type IconName } from '@/eyre/icons';
import { type EyreStringKey, useEyreT } from '@/eyre/strings';
import useApi from '@/hooks/use-api';
import useErrorHandler, { type ErrorHandlers } from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import ErrorPage from '@/pages/ErrorPage';
import PageMeta from '@/shared/components/PageMeta';
import { searchKeys } from '@/shared/utils/search-parameters';

import OrganizationSelector, { type Organization } from './OrganizationSelector';
import UnregisteredClientNotice from './UnregisteredClientNotice';
import styles from './index.module.scss';
import { getClientIdentifierHost, getRedirectUriOrigin } from './util';

/**
 * Eyre ID consent page, laid out like Google's "Sign in with Google" consent:
 * left column = "Sign in to <app>" + account chip, right column = what the app gets + Cancel / Continue.
 * Data flow (consent info, grant, access-denied, CIMD notice, organizations) is unchanged from Logto.
 */

const getClientDisplayData = ({ application: { id, displayName, name } }: ConsentInfoResponse) => {
  const unregisteredClientHost = isCimdClientId(id) ? getClientIdentifierHost(id) : undefined;

  return {
    unregisteredClientHost,
    applicationName: (displayName ?? name) || (unregisteredClientHost ?? name),
  };
};

const userScopeRows: Partial<Record<string, { icon: IconName; label: EyreStringKey }>> = {
  [UserScope.Profile]: { icon: 'accountCircle', label: 'profile' },
  [UserScope.Email]: { icon: 'mail', label: 'email' },
  [UserScope.Phone]: { icon: 'call', label: 'phone' },
  [UserScope.Address]: { icon: 'home', label: 'address' },
  [UserScope.CustomData]: { icon: 'dataObject', label: 'customData' },
  [UserScope.Identities]: { icon: 'link', label: 'identities' },
  [UserScope.Roles]: { icon: 'badge', label: 'roles' },
  [UserScope.Organizations]: { icon: 'groups', label: 'organizations' },
  [UserScope.OrganizationRoles]: { icon: 'groups', label: 'organizations' },
};

/** Fleet writes scope descriptions as "中文\nEnglish"; show the line for the current language. */
// eslint-disable-next-line @typescript-eslint/ban-types -- Logto scope descriptions can be null
const pickLine = (text: string | null | undefined, isZh: boolean) => {
  if (!text) {
    return '';
  }
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  return (isZh ? lines[0] : (lines[1] ?? lines[0])) ?? '';
};

const Consent = () => {
  const handleError = useErrorHandler();
  const asyncConsent = useApi(consent);
  const asyncGetConsentInfo = useApi(getConsentInfo);
  const redirectTo = useGlobalRedirectTo();
  const navigate = useNavigateWithPreservedSearchParams();
  const { t: logtoT } = useTranslation();
  const { t, isZh } = useEyreT();

  const [consentData, setConsentData] = useState<ConsentInfoResponse>();
  const [selectedOrganization, setSelectedOrganization] = useState<Organization>();
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [isConsentLoading, setIsConsentLoading] = useState(false);
  // Inner scroll of the right column (many permissions): edges show a divider while more content is hidden
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollEdges, setScrollEdges] = useState({ top: false, bottom: false });
  const updateScrollEdges = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = element;
    setScrollEdges({ top: scrollTop > 1, bottom: scrollTop + clientHeight < scrollHeight - 1 });
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    updateScrollEdges();
    const observer = new ResizeObserver(updateScrollEdges);
    observer.observe(element);
    for (const child of Array.from(element.children)) {
      observer.observe(child);
    }
    return () => {
      observer.disconnect();
    };
  });

  const consentErrorHandlers: ErrorHandlers = useMemo(
    () => ({
      'oidc.access_denied': () => {
        setIsAccessDenied(true);
      },
    }),
    []
  );

  const handleConsentError = useCallback(
    async (error: unknown) => {
      await handleError(error, consentErrorHandlers);
    },
    [consentErrorHandlers, handleError]
  );

  const signOut = useCallback(() => {
    const applicationId =
      new URLSearchParams(window.location.search).get(searchKeys.appId) ??
      consentData?.application.id;
    const signOutUrl = new URL('/oidc/session/end', window.location.origin);

    if (applicationId) {
      signOutUrl.searchParams.set('client_id', applicationId);
    }

    window.location.assign(signOutUrl.href);
  }, [consentData?.application.id]);

  const consentHandler = useCallback(async () => {
    setIsConsentLoading(true);
    const [error, result] = await asyncConsent(selectedOrganization?.id);
    setIsConsentLoading(false);

    if (error) {
      await handleConsentError(error);

      return;
    }

    if (result?.redirectTo) {
      await redirectTo(result.redirectTo);
    }
  }, [asyncConsent, handleConsentError, redirectTo, selectedOrganization?.id]);

  useEffect(() => {
    const getConsentInfoHandler = async () => {
      const [error, result] = await asyncGetConsentInfo();

      if (error) {
        await handleConsentError(error);

        return;
      }

      setConsentData(result);

      if (result?.organizations?.length) {
        setSelectedOrganization(result.organizations[0]);
      }
    };

    void getConsentInfoHandler();
  }, [asyncGetConsentInfo, handleConsentError]);

  if (isAccessDenied) {
    return (
      <ErrorPage
        isNavbarHidden
        title="error.access_denied"
        message="error.application_access_denied"
        primaryAction={{
          title: 'account_center.sessions.revoke_session',
          onClick: signOut,
        }}
      />
    );
  }

  if (!consentData) {
    return null;
  }

  const {
    user,
    redirectUri,
    application: { termsOfUseUrl, privacyPolicyUrl },
  } = consentData;
  const { unregisteredClientHost, applicationName } = getClientDisplayData(consentData);
  const redirectUriOrigin = redirectUri ? getRedirectUriOrigin(redirectUri) : undefined;
  const appLink = redirectUriOrigin?.startsWith('http') ? redirectUriOrigin : undefined;
  const appName = appLink ? (
    <a className={styles.appName} href={appLink} target="_blank" rel="noopener noreferrer">
      {applicationName}
    </a>
  ) : (
    <span className={styles.appName}>{applicationName}</span>
  );

  /** Render an Eyre string whose `{name}` slot holds the (link-styled) app name. */
  const withAppName = (key: EyreStringKey) => {
    const [before, after] = t(key, { name: '\u0000' }).split('\u0000');
    return (
      <>
        {before}
        {appName}
        {after}
      </>
    );
  };

  const identifier = user.primaryEmail ?? user.primaryPhone ?? user.username ?? user.id;
  type Row = { key: string; icon: IconName; title: string; sub?: string };
  const userRows = (consentData.missingOIDCScope ?? []).flatMap((scope): Row[] => {
    const row = userScopeRows[scope];
    if (scope === UserScope.Profile) {
      return [
        { key: scope, icon: 'accountCircle', title: user.name ?? identifier, sub: t('profile') },
      ];
    }
    if (scope === UserScope.Email && user.primaryEmail) {
      return [{ key: scope, icon: 'mail', title: user.primaryEmail, sub: t('email') }];
    }
    if (scope === UserScope.Phone && user.primaryPhone) {
      return [{ key: scope, icon: 'call', title: user.primaryPhone, sub: t('phone') }];
    }
    return [
      {
        key: scope,
        icon: row?.icon ?? 'key',
        title: row
          ? t(row.label)
          : String(
              logtoT(`user_scopes.descriptions.${scope}` as never, {
                nsSeparator: '|',
                defaultValue: scope,
              })
            ),
      },
    ];
  });
  // De-duplicate rows that map to the same label (organizations + organization roles)
  const seen = new Set<string>();
  const uniqueUserRows = userRows.filter(
    ({ title }) => !seen.has(title) && Boolean(seen.add(title))
  );

  const resourceGroups = (consentData.missingResourceScopes ?? []).filter(
    ({ resource }) => resource.id !== ReservedResource.Organization
  );
  const hasAnything = uniqueUserRows.length > 0 || resourceGroups.length > 0;

  const policyLinks = [
    privacyPolicyUrl && { href: privacyPolicyUrl, label: t('privacy') },
    termsOfUseUrl && { href: termsOfUseUrl, label: t('terms') },
  ].filter((link): link is { href: string; label: string } => Boolean(link));

  return (
    <div className={styles.grid}>
      <PageMeta
        titleKey="description.authorize_title"
        titleKeyInterpolation={{ name: applicationName }}
      />
      <section className={styles.left}>
        <h1 className={styles.title}>{withAppName('consentTitle')}</h1>
        <button
          type="button"
          className={styles.chip}
          title={t('useAnotherAccount')}
          onClick={() => {
            navigate('/sign-in', { replace: true });
          }}
        >
          {user.avatar ? (
            <img src={user.avatar} alt="" className={styles.chipAvatar} />
          ) : (
            <UserAvatar className={styles.chipAvatar} />
          )}
          <span className={styles.chipText}>{identifier}</span>
          <Icon name="arrowDropDown" className={styles.chipCaret} />
        </button>
        {unregisteredClientHost && (
          <UnregisteredClientNotice className={styles.notice} host={unregisteredClientHost} />
        )}
      </section>

      <section className={styles.right}>
        <div
          ref={scrollRef}
          className={styles.scroll}
          data-top={scrollEdges.top}
          data-bottom={scrollEdges.bottom}
          onScroll={updateScrollEdges}
        >
          <h2 className={styles.subtitle}>
            {withAppName(hasAnything ? 'consentAllow' : 'consentNothing')}
          </h2>

          {uniqueUserRows.length > 0 && (
            <ul className={styles.rows}>
              {uniqueUserRows.map(({ key, icon, title, sub }) => (
                <li key={key} className={styles.row}>
                  <Icon name={icon} className={styles.rowIcon} />
                  <div>
                    <div className={styles.rowTitle}>{title}</div>
                    {sub && <div className={styles.rowSub}>{sub}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {resourceGroups.length > 0 && (
            <ul className={styles.rows}>
              {resourceGroups.map(({ resource, scopes }) => (
                <li key={resource.id} className={styles.row}>
                  <Icon name="key" className={styles.rowIcon} />
                  <div>
                    <div className={styles.rowTitle}>{resource.name}</div>
                    <ul className={styles.scopeList}>
                      {scopes.map(({ id, name, description }) => (
                        <li key={id}>{pickLine(description, isZh) || name}</li>
                      ))}
                    </ul>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {consentData.organizations && (
            <OrganizationSelector
              className={styles.organizations}
              organizations={consentData.organizations}
              selectedOrganization={selectedOrganization}
              onSelect={setSelectedOrganization}
            />
          )}

          <div className={styles.legal}>
            {policyLinks.length > 0 && (
              <p>
                {(() => {
                  const [before, after] = t('consentPolicy', {
                    name: applicationName,
                    links: '\u0000',
                  }).split('\u0000');
                  return (
                    <>
                      {before}
                      {policyLinks.map(({ href, label }, index) => (
                        <Fragment key={href}>
                          {index > 0 && ` ${t('and')} `}
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {isZh ? `《${label}》` : label}
                          </a>
                        </Fragment>
                      ))}
                      {after}
                    </>
                  );
                })()}
              </p>
            )}
            <p>{t('consentNotice', { name: applicationName })}</p>
            {redirectUriOrigin && <p>{t('consentRedirect', { origin: redirectUriOrigin })}</p>}
          </div>
        </div>

        <div className={styles.actions} data-divider={scrollEdges.bottom}>
          {redirectUri && (
            <button
              type="button"
              className={styles.outlined}
              onClick={() => {
                window.location.replace(redirectUri);
              }}
            >
              {t('cancel')}
            </button>
          )}
          <button
            type="button"
            className={styles.outlined}
            disabled={isConsentLoading}
            onClick={() => {
              void consentHandler();
            }}
          >
            {isConsentLoading ? <span className={styles.spinner} /> : t('continue')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Consent;
